// Preserves the user's in-app location across an involuntary logout (idle
// timeout / failed token refresh) so they land back where they were after
// re-authenticating — instead of the default project page (issue #1776).
//
// sessionStorage (not localStorage) is deliberate: it survives the
// logout→Keycloak→login full-page round-trip within the same tab, but is gone
// once the user closes the tab and starts fresh, so a stale location is never
// restored later. The nav-menu logout never calls captureLocationForRestore,
// so a deliberate logout always lands on the default page.
//
// Restore is performed by the router guard (router/index.ts), not here:
// createWebHistory() snapshots window.location when the router module is
// imported, so rewriting the URL afterwards has no effect on the initial
// navigation. The guard consumes the stash on the root-redirect instead.

const POST_LOGOUT_REDIRECT_KEY = 'turing:post-logout-redirect'

// Landing / auth-utility routes that make no sense to restore to.
const NON_RESTORABLE_PATHS = new Set([
  '/',
  '/select-organization',
  '/no-access',
  '/no-organization-access',
  '/accept-invitation'
])

// Records the current location so it can be restored after re-login. Skips
// landing/utility routes (nothing worth returning to) and silently no-ops when
// sessionStorage is unavailable (private mode / quota).
export const captureLocationForRestore = (): void => {
  try {
    if (NON_RESTORABLE_PATHS.has(window.location.pathname)) return
    sessionStorage.setItem(POST_LOGOUT_REDIRECT_KEY, window.location.pathname + window.location.search)
  }
  catch {
    // sessionStorage unavailable — restore is best-effort, fall back to default page.
  }
}

// Reads and clears the stashed location. One-shot by design. Returns null when
// nothing is stashed or sessionStorage is unavailable.
export const consumePostLogoutRedirect = (): string | null => {
  try {
    const path = sessionStorage.getItem(POST_LOGOUT_REDIRECT_KEY)
    if (path) sessionStorage.removeItem(POST_LOGOUT_REDIRECT_KEY)
    return path
  }
  catch {
    return null
  }
}
